import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ClickAwayListener, Popper } from '@mui/material';
import { API_BASE_URL, fetchJson } from './api';
import { DARK_MODE_EVENT, getDarkMode } from './darkMode';

// v2: notifications are ephemeral (show once, then clear).
// Bump keys so legacy cached "read" notifications don't keep resurfacing.
const STORAGE_KEY = 'monsurv_notifications_v2';
const SEEN_KEY = 'monsurv_notifications_seen_v2';
const USER_PREFS_CACHE_KEY = 'monsurv_user_preferences_v1';
const MAX_ITEMS = 200;

function loadNotificationPrefsFromStorage() {
    try {
        const raw = localStorage.getItem(USER_PREFS_CACHE_KEY);
        const parsed = raw ? safeJsonParse(raw, null) : null;
        const n = parsed && typeof parsed === 'object' ? parsed.notifications : null;
        return {
            enabled: n && typeof n.enabled === 'boolean' ? n.enabled : true,
            poll_interval_seconds: Number.isFinite(Number(n?.poll_interval_seconds)) ? Number(n.poll_interval_seconds) : 15,
            toast_enabled: n && typeof n.toast_enabled === 'boolean' ? n.toast_enabled : true,
            quiet_hours_start: typeof n?.quiet_hours_start === 'string' ? n.quiet_hours_start : '',
            quiet_hours_end: typeof n?.quiet_hours_end === 'string' ? n.quiet_hours_end : '',
            types: (n && n.types && typeof n.types === 'object') ? n.types : {}
        };
    } catch {
        return { enabled: true, poll_interval_seconds: 15, toast_enabled: true, quiet_hours_start: '', quiet_hours_end: '', types: {} };
    }
}

function parseHHMMToMinutes(value) {
    const v = String(value || '').trim();
    if (!v) return null;
    const m = v.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    if (!m) return null;
    return Number(m[1]) * 60 + Number(m[2]);
}

function isWithinQuietHours(now, startHHMM, endHHMM) {
    const start = parseHHMMToMinutes(startHHMM);
    const end = parseHHMMToMinutes(endHHMM);
    if (start == null || end == null) return false;
    if (start === end) return false;
    const cur = now.getHours() * 60 + now.getMinutes();
    if (start < end) return cur >= start && cur < end;
    // Wrap midnight
    return cur >= start || cur < end;
}

function normalizeTypeToPreferenceKey(type) {
    const t = String(type || '').trim().toLowerCase();
    if (!t) return null;
    if (t.includes('drawdown')) return 'drawdown_requested';
    if (t.includes('report')) return 'report_ready';
    if (t.includes('questionnaire')) return 'questionnaire_submitted';
    if (t.includes('issue')) return 'issue_reported';
    if (t.includes('client') && t.includes('approval')) return 'client_approval_requested';
    return null;
}

function safeJsonParse(raw, fallback) {
    try {
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
}

function loadStoredItems() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = safeJsonParse(raw || '[]', []);
        if (!Array.isArray(parsed)) return [];
        return parsed
            // Only restore client-side notifications (server notifications are refreshed from the API).
            .filter((x) => x && typeof x === 'object' && typeof x.message === 'string' && !Boolean(x.read) && !String(x.id || '').startsWith('srv_'))
            .slice(0, MAX_ITEMS);
    } catch {
        return [];
    }
}

function loadSeenKeys() {
    try {
        const raw = localStorage.getItem(SEEN_KEY);
        const parsed = safeJsonParse(raw || '[]', []);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((x) => typeof x === 'string').slice(0, MAX_ITEMS);
    } catch {
        return [];
    }
}

function getAuthToken() {
    try {
        return String(localStorage.getItem('monsurv_auth_token') || '').trim();
    } catch {
        return '';
    }
}

function isPortalToken(token) {
    try {
        const parts = String(token || '').split('.');
        if (parts.length < 2) return false;
        const raw = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = raw.padEnd(Math.ceil(raw.length / 4) * 4, '=');
        const json = atob(padded);
        const payload = JSON.parse(json);
        return Boolean(payload?.portal);
    } catch {
        return false;
    }
}

function tryParseServerNotificationId(clientId) {
    const raw = String(clientId || '').trim();
    if (!raw.startsWith('srv_')) return null;
    const n = Number(raw.slice(4));
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.trunc(n);
}

async function markServerNotificationRead({ token, clientId }) {
    const id = tryParseServerNotificationId(clientId);
    if (!id) return;
    if (!token) return;
    await fetchJson(`${API_BASE_URL}/api/notifications/${id}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
    });
}

async function markAllServerNotificationsRead({ token }) {
    if (!token) return;
    await fetchJson(`${API_BASE_URL}/api/notifications/read-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
    });
}

function tryGetDashboardRoleFromToken(token) {
    try {
        const parts = String(token || '').split('.');
        if (parts.length < 2) return null;
        const raw = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = raw.padEnd(Math.ceil(raw.length / 4) * 4, '=');
        const json = atob(padded);
        const payload = JSON.parse(json);
        const role = String(payload?.role || '').trim().toLowerCase();
        return role || null;
    } catch {
        return null;
    }
}

function saveStoredItems(items) {
    try {
        const list = Array.isArray(items) ? items : [];
        const clientOnly = list.filter((x) => !String(x?.id || '').startsWith('srv_'));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(clientOnly.slice(0, MAX_ITEMS)));
    } catch {
        // ignore
    }
}

function saveSeenKeys(keys) {
    try {
        localStorage.setItem(SEEN_KEY, JSON.stringify(keys.slice(0, MAX_ITEMS)));
    } catch {
        // ignore
    }
}

function createId() {
    try {
        // Avoid `globalThis` for older ESLint configs.
        const c = (typeof window !== 'undefined' && window.crypto) ? window.crypto : null;
        if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    } catch {
        // ignore
    }
    return `n_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
    const [items, setItems] = useState(() => loadStoredItems());
    const [seenKeys, setSeenKeys] = useState(() => loadSeenKeys());
    const seenKeysRef = useRef(seenKeys);
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);
    const [notifPrefs, setNotifPrefs] = useState(() => loadNotificationPrefsFromStorage());

    useEffect(() => {
        function handlePrefsUpdated(e) {
            const p = e?.detail && typeof e.detail === 'object' ? e.detail : null;
            const n = p && typeof p.notifications === 'object' ? p.notifications : null;
            if (!n) {
                setNotifPrefs(loadNotificationPrefsFromStorage());
                return;
            }
            setNotifPrefs(loadNotificationPrefsFromStorage());
        }

        window.addEventListener('monsurv:user-preferences-updated', handlePrefsUpdated);
        return () => window.removeEventListener('monsurv:user-preferences-updated', handlePrefsUpdated);
    }, []);

    function toKey(item) {
        const type = String(item?.type || 'info').trim().toLowerCase();
        const ref = String(item?.project_reference || '').trim().toLowerCase();
        const msg = String(item?.message || '').trim().toLowerCase();
        return `${type}||${ref}||${msg}`;
    }

    useEffect(() => {
        saveStoredItems(items);
    }, [items]);

    useEffect(() => {
        seenKeysRef.current = seenKeys;
        saveSeenKeys(seenKeys);
    }, [seenKeys]);

    useEffect(() => {
        let cancelled = false;
        let timer = null;

        const enabled = Boolean(notifPrefs?.enabled);
        const pollSeconds = Math.max(15, Math.min(300, Number(notifPrefs?.poll_interval_seconds || 60)));

        function canIncludeByType(itemType) {
            const prefKey = normalizeTypeToPreferenceKey(itemType);
            if (!prefKey) return true;

            // Safety: client approval notifications are admin-only.
            if (prefKey === 'client_approval_requested') {
                const role = tryGetDashboardRoleFromToken(getAuthToken());
                if (role !== 'admin') return false;
            }

            const map = notifPrefs && notifPrefs.types && typeof notifPrefs.types === 'object' ? notifPrefs.types : {};
            if (map[prefKey] === false) return false;
            return true;
        }

        // Prune any cached items that the current user should not see.
        setItems((prev) => {
            const existing = Array.isArray(prev) ? prev : [];
            const filtered = existing.filter((x) => canIncludeByType(x?.type));
            return filtered.length === existing.length ? existing : filtered;
        });

        function canToastNow(item) {
            if (!Boolean(notifPrefs?.toast_enabled)) return false;
            if (!enabled) return false;
            if (!canIncludeByType(item?.type)) return false;
            const now = new Date();
            if (isWithinQuietHours(now, notifPrefs?.quiet_hours_start, notifPrefs?.quiet_hours_end)) return false;
            return true;
        }

        function showToast(next) {
            setToast(next);
            if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
            toastTimerRef.current = window.setTimeout(() => {
                setToast(null);
                toastTimerRef.current = null;
            }, 2600);
        }

        async function loadServerNotifications() {
            try {
                if (!enabled) return;
                const token = getAuthToken();
                if (!token || isPortalToken(token)) return;

                const json = await fetchJson(`${API_BASE_URL}/api/notifications?limit=100&unread_only=1`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` },
                    cache: 'no-store'
                });

                const rows = Array.isArray(json?.items) ? json.items : [];
                if (cancelled || !rows.length) return;

                let toastCandidate = null;
                setItems((prev) => {
                    const existing = Array.isArray(prev) ? prev : [];
                    const existingIds = new Set(existing.map((x) => x.id));
                    const existingKeys = new Set(existing.map((x) => toKey(x)));
                    const seen = new Set(Array.isArray(seenKeysRef.current) ? seenKeysRef.current : []);
                    const mapped = rows.map((r) => ({
                        id: `srv_${r.id}`,
                        created_at: r.created_at,
                        // We request unread-only from the server; treat all fetched items as unread.
                        read: false,
                        type: String(r.type || 'info'),
                        project_reference: String(r.project_reference || ''),
                        message: String(r.message || ''),
                        payload: (r && typeof r === 'object') ? (r.payload ?? null) : null
                    }));

                    const next = [...existing];
                    for (const item of mapped) {
                        if (!item.message || existingIds.has(item.id)) continue;
                        if (!canIncludeByType(item.type)) continue;
                        const key = toKey(item);
                        if (existingKeys.has(key) || seen.has(key)) continue;
                        existingKeys.add(key);
                        next.unshift(item);
                        if (!toastCandidate) toastCandidate = item;
                    }
                    return next.slice(0, MAX_ITEMS);
                });

                if (toastCandidate && canToastNow(toastCandidate)) showToast(toastCandidate);
            } catch {
                // ignore
            }
        }

        if (!enabled) return () => { cancelled = true; };

        function onFocus() {
            loadServerNotifications();
        }

        function onVisibilityChange() {
            try {
                if (document.visibilityState === 'visible') loadServerNotifications();
            } catch {
                // ignore
            }
        }

        loadServerNotifications();
        timer = window.setInterval(loadServerNotifications, pollSeconds * 1000);
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            cancelled = true;
            if (timer) window.clearInterval(timer);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [notifPrefs]);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                window.clearTimeout(toastTimerRef.current);
                toastTimerRef.current = null;
            }
        };
    }, []);

    const unreadCount = useMemo(() => items.length, [items]);

    const addSeenKeys = useCallback((nextItems) => {
        const list = Array.isArray(nextItems) ? nextItems : [];
        if (!list.length) return;
        setSeenKeys((prev) => {
            const next = new Set(Array.isArray(prev) ? prev : []);
            for (const item of list) {
                next.add(toKey(item));
            }
            return Array.from(next).slice(0, MAX_ITEMS);
        });
    }, []);

    const notify = useCallback(({ message, projectReference, type } = {}) => {
        const msg = String(message || '').trim();
        if (!msg) return;

        const next = {
            id: createId(),
            created_at: new Date().toISOString(),
            read: false,
            type: String(type || 'info'),
            project_reference: projectReference ? String(projectReference).trim() : '',
            message: msg
        };

        setItems((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const key = toKey(next);
            if (list.some((x) => toKey(x) === key)) return list;
            return [next, ...list].slice(0, MAX_ITEMS);
        });

        // Toast: show briefly at top, then hide.
        if (Boolean(notifPrefs?.toast_enabled) && Boolean(notifPrefs?.enabled)) {
            const now = new Date();
            const prefKey = normalizeTypeToPreferenceKey(next.type);
            const map = notifPrefs && notifPrefs.types && typeof notifPrefs.types === 'object' ? notifPrefs.types : {};
            const allowed = prefKey ? (map[prefKey] !== false) : true;
            if (allowed && !isWithinQuietHours(now, notifPrefs?.quiet_hours_start, notifPrefs?.quiet_hours_end)) {
                setToast(next);
                if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
                toastTimerRef.current = window.setTimeout(() => {
                    setToast(null);
                    toastTimerRef.current = null;
                }, 2600);
            }
        }
    }, [notifPrefs]);

    // "markRead" is retained for backwards compatibility within this module,
    // but its behavior is now "clear on click" (show once).
    const markRead = useCallback((id) => {
        const token = getAuthToken();
        if (token && !isPortalToken(token)) {
            // Best-effort (do not block UI)
            markServerNotificationRead({ token, clientId: id }).catch(() => { });
        }

        setItems((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const item = list.find((x) => x.id === id);
            if (item) addSeenKeys([item]);
            return list.filter((x) => x.id !== id);
        });
    }, [addSeenKeys]);

    const clearAll = useCallback(() => {
        const token = getAuthToken();
        if (token && !isPortalToken(token)) {
            // Persist "cleared" across sessions/devices.
            markAllServerNotificationsRead({ token }).catch(() => { });
        }
        setItems((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            addSeenKeys(list);
            return [];
        });
        setToast(null);
    }, [addSeenKeys]);

    const value = useMemo(
        () => ({ items, unreadCount, toast, notify, markRead, clearAll }),
        [items, unreadCount, toast, notify, markRead, clearAll]
    );

    return (
        <NotificationsContext.Provider value={value}>
            {children}
            <NotificationsToaster toast={toast} />
        </NotificationsContext.Provider>
    );
}

export function useNotifications() {
    const ctx = useContext(NotificationsContext);
    if (!ctx) {
        return {
            items: [],
            unreadCount: 0,
            toast: null,
            notify: () => { },
            markRead: () => { },
            clearAll: () => { }
        };
    }
    return ctx;
}

function BellIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
                d="M12 22c1.2 0 2.2-1 2.2-2.2h-4.4C9.8 21 10.8 22 12 22Zm7-6V11c0-3.3-2.1-6.1-5.2-7.1V3a1.8 1.8 0 0 0-3.6 0v.9C7.1 4.9 5 7.7 5 11v5l-1.7 1.7a1 1 0 0 0 .7 1.7h16a1 1 0 0 0 .7-1.7L19 16Z"
                fill="currentColor"
            />
        </svg>
    );
}

function formatTime(iso) {
    try {
        const dt = new Date(iso);
        if (!Number.isFinite(dt.getTime())) return '';
        return dt.toLocaleString();
    } catch {
        return '';
    }
}

export function NotificationBell({ compact, variant = 'default' } = {}) {
    const { items, unreadCount, markRead, clearAll } = useNotifications();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const isHeaderVariant = variant === 'header';
    const [dark, setDark] = useState(() => getDarkMode());

    useEffect(() => {
        function syncDarkMode(event) {
            const next = typeof event?.detail?.dark === 'boolean' ? event.detail.dark : getDarkMode();
            setDark(next);
        }

        try {
            window.addEventListener(DARK_MODE_EVENT, syncDarkMode);
            return () => window.removeEventListener(DARK_MODE_EVENT, syncDarkMode);
        } catch {
            return undefined;
        }
    }, []);

    const colors = useMemo(() => {
        if (dark) {
            return {
                buttonBg: isHeaderVariant
                    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.92) 0%, rgba(22, 32, 51, 0.84) 100%)'
                    : 'linear-gradient(145deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 64, 175, 0.78) 56%, rgba(15, 118, 110, 0.72) 100%)',
                buttonBorder: '1px solid rgba(226, 232, 240, 0.14)',
                buttonShadow: isHeaderVariant ? 'inset 0 1px 0 rgba(255,255,255,0.04), 0 14px 30px rgba(2, 6, 23, 0.3)' : '0 18px 42px rgba(2, 6, 23, 0.28)',
                buttonText: '#e5e7eb',
                panelBg: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(17, 24, 39, 0.96) 100%)',
                panelBorder: '1px solid rgba(226, 232, 240, 0.12)',
                panelShadow: isHeaderVariant ? '0 28px 72px rgba(2, 6, 23, 0.5)' : '0 24px 64px rgba(2, 6, 23, 0.4)',
                panelHeaderBg: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 64, 175, 0.88) 56%, rgba(15, 118, 110, 0.76) 100%)',
                divider: 'rgba(226, 232, 240, 0.08)',
                title: '#e5e7eb',
                muted: '#94a3b8',
                rowBg: 'rgba(17, 24, 39, 0.78)',
                rowBorder: 'rgba(226, 232, 240, 0.08)',
                rowHover: 'rgba(30, 41, 59, 0.92)',
                actionBg: 'rgba(30, 41, 59, 0.92)',
                actionBorder: 'rgba(226, 232, 240, 0.12)',
                actionText: '#e5e7eb'
            };
        }

        return {
            buttonBg: isHeaderVariant
                ? 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(240,246,251,0.72) 100%)'
                : 'linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(239,245,251,0.9) 100%)',
            buttonBorder: isHeaderVariant ? '1px solid rgba(255,255,255,0.58)' : '1px solid rgba(203, 213, 225, 0.8)',
            buttonShadow: isHeaderVariant ? 'inset 0 1px 0 rgba(255,255,255,0.68), 0 10px 26px rgba(15, 23, 42, 0.08)' : '0 14px 30px rgba(15, 23, 42, 0.12)',
            buttonText: '#0f172a',
            panelBg: isHeaderVariant
                ? 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(244,248,252,0.94) 100%)'
                : '#fff',
            panelBorder: isHeaderVariant ? '1px solid rgba(255,255,255,0.62)' : '1px solid #e5e7eb',
            panelShadow: isHeaderVariant ? '0 22px 56px rgba(15, 23, 42, 0.18)' : '0 18px 46px rgba(15, 23, 42, 0.16)',
            panelHeaderBg: 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 64, 175, 0.88) 56%, rgba(15, 118, 110, 0.72) 100%)',
            divider: '#f3f4f6',
            title: '#0f172a',
            muted: '#6b7280',
            rowBg: 'rgba(255,255,255,0.88)',
            rowBorder: '#e2e8f0',
            rowHover: '#f8fafc',
            actionBg: 'rgba(255,255,255,0.92)',
            actionBorder: '#cbd5e1',
            actionText: '#0f172a'
        };
    }, [dark, isHeaderVariant]);

    function buildOpenHref(n) {
        try {
            const t = String(n?.type || '').trim().toLowerCase();
            if (t === 'issue_reported') {
                const qs = new URLSearchParams(window.location.search || '');
                qs.set('view', 'issue_reports');
                qs.set('issueReportId', String(n?.id ?? ''));
                return `${window.location.pathname}?${qs.toString()}`;
            }

            if (t === 'issue_resolved') {
                const issueId = n?.payload?.issue_report_id ?? n?.payload?.issueReportId ?? null;
                const qs = new URLSearchParams(window.location.search || '');
                qs.set('view', 'issue_reports');
                if (issueId != null && String(issueId).trim() !== '') qs.set('issueReportId', String(issueId));
                return `${window.location.pathname}?${qs.toString()}`;
            }

            const ref = String(n?.project_reference || n?.payload?.project_reference || '').trim();
            if (!ref) return null;

            const qs = new URLSearchParams(window.location.search || '');
            qs.set('view', 'reports');
            qs.set('ref', ref);

            const reportLogId = n?.payload?.report_log_id ?? n?.payload?.reportLogId ?? null;
            if (reportLogId != null && String(reportLogId).trim() !== '') {
                qs.set('reportLogId', String(reportLogId));
                if (t.startsWith('report_')) {
                    qs.set('openChanges', '1');
                }
            }

            return `${window.location.pathname}?${qs.toString()}`;
        } catch {
            return null;
        }
    }

    return (
        <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="Notifications"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: compact ? 0 : 8,
                    width: compact ? 30 : 'auto',
                    minWidth: compact ? 30 : 92,
                    height: compact ? 30 : 42,
                    padding: compact ? 0 : '0 12px',
                    borderRadius: 999,
                    border: colors.buttonBorder,
                    background: colors.buttonBg,
                    boxShadow: colors.buttonShadow,
                    cursor: 'pointer',
                    position: 'relative',
                    color: colors.buttonText,
                    overflow: 'visible',
                    transition: 'transform 160ms ease, box-shadow 160ms ease'
                }}
                title={unreadCount ? `${unreadCount} unread notifications` : 'Notifications'}
            >
                <BellIcon size={compact ? 14 : 18} />
                {compact ? null : (
                    <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: colors.buttonText }}>
                        Alerts
                    </span>
                )}
                {unreadCount ? (
                    <span
                        style={{
                            position: 'absolute',
                            top: compact ? -2 : -3,
                            right: compact ? -2 : -3,
                            minWidth: compact ? 14 : 18,
                            height: compact ? 14 : 18,
                            padding: compact ? '0 3px' : '0 5px',
                            borderRadius: 999,
                            background: '#dc2626',
                            color: '#fff',
                            fontSize: compact ? 9 : 11,
                            fontWeight: 900,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: compact ? '1px solid #fff' : '2px solid #fff',
                            lineHeight: compact ? '14px' : '18px'
                        }}
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                ) : null}
            </button>

            <Popper
                open={open}
                anchorEl={rootRef.current}
                placement="bottom-end"
                modifiers={[
                    { name: 'offset', options: { offset: [0, 10] } },
                    { name: 'preventOverflow', options: { padding: 12 } },
                    { name: 'flip', options: { padding: 12 } }
                ]}
                sx={{ zIndex: 1400 }}
            >
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <div
                        style={{
                            width: compact ? 'min(340px, calc(100vw - 24px))' : 'min(420px, calc(100vw - 24px))',
                            background: colors.panelBg,
                            border: colors.panelBorder,
                            borderRadius: compact ? 18 : 22,
                            boxShadow: colors.panelShadow,
                            backdropFilter: isHeaderVariant ? 'blur(14px)' : undefined,
                            WebkitBackdropFilter: isHeaderVariant ? 'blur(14px)' : undefined,
                            overflow: 'hidden'
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: compact ? 6 : 8,
                                padding: compact ? 12 : 14,
                                borderBottom: `1px solid ${colors.divider}`,
                                background: colors.panelHeaderBg,
                                color: '#f8fafc'
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <div
                                    style={{
                                        fontWeight: 900,
                                        fontSize: compact ? 12 : 13,
                                        color: '#f8fafc',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    Alerts
                                </div>
                                <div style={{ fontSize: compact ? 10 : 11, color: 'rgba(248, 250, 252, 0.78)' }}>
                                    {unreadCount ? `${unreadCount} unread item${unreadCount === 1 ? '' : 's'}` : 'Everything is up to date'}
                                </div>
                            </div>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: compact ? 6 : 8 }}>
                                <button type="button" onClick={clearAll} style={{ fontSize: compact ? 11 : 12, padding: compact ? '4px 9px' : '6px 12px', whiteSpace: 'nowrap', color: '#f8fafc', border: '1px solid rgba(248, 250, 252, 0.22)', background: 'rgba(255,255,255,0.08)', borderRadius: 999, cursor: 'pointer', fontWeight: 800 }}>Clear</button>
                            </div>
                        </div>

                        <div style={{ maxHeight: 420, overflow: 'auto', padding: compact ? 8 : 10, background: colors.panelBg }}>
                            {!items.length ? (
                                <div style={{ padding: compact ? 14 : 16, color: colors.muted, fontSize: compact ? 12 : 13, borderRadius: 16, border: `1px solid ${colors.rowBorder}`, background: colors.rowBg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>No notifications yet.</div>
                            ) : (
                                items.map((n) => {
                                    const openHref = buildOpenHref(n);
                                    return (
                                        <div
                                            key={n.id}
                                            onClick={() => markRead(n.id)}
                                            style={{
                                                padding: compact ? 12 : 14,
                                                marginBottom: compact ? 8 : 10,
                                                border: `1px solid ${colors.rowBorder}`,
                                                borderRadius: compact ? 16 : 18,
                                                cursor: 'pointer',
                                                background: colors.rowBg,
                                                boxShadow: dark ? '0 14px 28px rgba(2, 6, 23, 0.18)' : '0 12px 24px rgba(15, 23, 42, 0.08)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                <span style={{ width: 8, height: 8, borderRadius: 999, background: '#ef4444', boxShadow: '0 0 0 4px rgba(239, 68, 68, 0.12)', flexShrink: 0 }} />
                                                <span style={{ fontSize: compact ? 10 : 11, fontWeight: 800, color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                    {String(n.type || 'alert').replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                            <div
                                                style={{
                                                    fontWeight: 900,
                                                    fontSize: compact ? 12 : 13,
                                                    color: colors.title,
                                                    whiteSpace: 'normal',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    lineHeight: 1.45
                                                }}
                                            >
                                                {n.message}
                                            </div>
                                            <div style={{ marginTop: compact ? 6 : 8, fontSize: compact ? 11 : 12, color: colors.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatTime(n.created_at)}</div>
                                            {openHref ? (
                                                <div style={{ marginTop: compact ? 6 : 8, display: 'flex', justifyContent: 'flex-end' }}>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            markRead(n.id);
                                                            setOpen(false);
                                                            window.location.assign(openHref);
                                                        }}
                                                        style={{
                                                            fontSize: compact ? 11 : 12,
                                                            padding: compact ? '5px 10px' : '7px 12px',
                                                            fontWeight: 800,
                                                            color: colors.actionText,
                                                            border: `1px solid ${colors.actionBorder}`,
                                                            background: colors.actionBg,
                                                            borderRadius: 999,
                                                            cursor: 'pointer',
                                                            boxShadow: dark ? '0 10px 18px rgba(2, 6, 23, 0.16)' : '0 8px 18px rgba(15, 23, 42, 0.08)'
                                                        }}
                                                    >
                                                        Open
                                                    </button>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </ClickAwayListener>
            </Popper>
        </div>
    );
}

function NotificationsToaster({ toast }) {
    if (!toast) return null;

    const topPx = (() => {
        try {
            const smUp = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
                ? window.matchMedia('(min-width:600px)').matches
                : true;
            // Keep in sync with Dashboard/PortalShell AppBar Toolbar minHeight.
            const headerHeight = smUp ? 78 : 56;
            return headerHeight + 10;
        } catch {
            return 66;
        }
    })();

    return (
        <div
            style={{
                position: 'fixed',
                top: `calc(${topPx}px + env(safe-area-inset-top, 0px))`,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 100,
                width: 'min(720px, calc(100vw - 24px))'
            }}
        >
            <div
                style={{
                    background: '#111827',
                    color: '#fff',
                    padding: '10px 12px',
                    borderRadius: 12,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
                    fontWeight: 800
                }}
                role="status"
                aria-live="polite"
            >
                {toast.message}
            </div>
        </div>
    );
}
